/* eslint-disable max-lines-per-function */
import { gql, useMutation } from "@apollo/client"
import { Box, Button, Flex, Heading, Text, useToast } from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { RHeading3PT } from "../../components/commons/RHeading3PT"
import { FormElement } from "../../components/commons/globalFormComponent"
import { Colors } from "../../theme/colors"
import { ModalThreeRowLayout } from "../../components/commons/modalThreeRowLayout"

const RESET_PASSWORD = gql`
  mutation SendResetPasswordLink($email_address: String!) {
    sendResetPasswordLink(email_address: $email_address)
  }
`
export const ResetPassword = ({ onBack }: { onBack: () => void }): JSX.Element => {
  const toast = useToast()

  const [resendEmail, { data: resendSuccess, loading: resendLoading, error: resendError }] = useMutation(RESET_PASSWORD)
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    const formElement = event.currentTarget as HTMLFormElement
    const email = formElement.email.value
    localStorage.setItem("reset_password_email_address", email)
    resendEmail({ variables: { email_address: email } }).catch(err => {
      console.error(err)
    })
    event.preventDefault()
  }
  useEffect(() => {
    document.body.style.backgroundColor = Colors.Secondary.c2 ?? ""

    return () => {
      document.body.style.backgroundColor = "white"
    }
  }, [])

  const resendLink = (): void => {
    // temp code
    const localStorageResetEmail = localStorage.getItem("reset_password_email_address")
    if (localStorageResetEmail) {
      resendEmail({
        variables: { email_address: localStorageResetEmail }
      }).catch(err => {
        console.error(err)
      })
    } else {
      toast({
        title: "Error",
        description: "Invalid request - ERR01",
        status: "error",
        isClosable: true
      })
    }
  }

  useEffect(() => {
    // if (data?.sendResetPasswordLink) {
    if (resendSuccess) {
      toast({
        title: "Success",
        description: "Reset password link sent successfully. Please check your email for the verification link.",
        status: "success",
        isClosable: true,
        duration: 3000
      })
    }
  }, [resendSuccess, toast])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if ((resendError && resendError.graphQLErrors.length > 0) || resendError?.message != null) {
      toast({
        title: "Error",
        description: (resendError.graphQLErrors[0] as unknown as string) ?? `Resend Error: ${resendError?.message}`,
        status: "error",
        isClosable: true
      })
    }
  }, [resendError, toast])
  const [emailAddress, setEmailAddress] = useState<string>("")

  return (
    <ModalThreeRowLayout
      middleComponent={
        <>
          {resendSuccess && (
            <Flex flexDir="column" maxW={["328px", "328px", "576px", "576px", "576px"]} alignItems={"center"}>
              <Heading
                color="white"
                mb="32px"
                mt={["104px", "104px", "104px", "32px", "32px", "32px"]}
                display={"flex"}
                alignItems="center"
                textAlign={"center"}
                height={["auto", "auto", "auto", "160px", "160px"]}
                variant="Heading1"
                lineHeight={["40px", "40px", "40px", "68px", "68px"]}
                fontSize={["40px", "40px", "40px", "60px", "60px"]}
                width={["auto", "auto", "auto", "576px"]}
                maxW={["328px", "328px", "576px", "576px", "576px"]}
              >
                Password Link Sent
              </Heading>
              <Box width={["100%"]} maxW={"328px"}>
                <Text mt={["33px", "33px", "10px"]} color="white">
                  We&apos;ve sent a reset link to the email address you entered. Please click the link in your email to change your password.
                </Text>
                <Text color="white" mt="11px">
                  The email may take a few minutes to send.
                </Text>

                <Text mt={["72px", "80px", "76px"]} color="white">
                  If you&apos;re having trouble receiving the link, click the button below:
                </Text>
                <Button
                  mt="20px"
                  variant="Button1RegularOutline"
                  onClick={() => {
                    resendLink()
                  }}
                  isLoading={resendLoading}
                >
                  Resend Link
                </Button>
              </Box>
            </Flex>
          )}
          {!resendSuccess && (
            <>
              <RHeading3PT text="Reset Password" props={{ color: "white", my: "32px" }} />
              <Text color={"white"} mt={["min(2vh, 32px)"]} variant={"RBody"} align={"center"} maxW="254px">
                To reset your password, please enter your email address below. If it is in our database, a reset link will be sent there shortly.
              </Text>
              <Box maxW={"250px"} margin={"auto"} mt="24px">
                <form onSubmit={handleSubmit} method="post">
                  <FormElement
                    name="email"
                    type="email"
                    placeHolder="Your Email"
                    label="Email Address"
                    htmlValidation={{ required: true }}
                    onChangeCallBack={value => {
                      setEmailAddress(value)
                    }}
                    value={emailAddress}
                    formControlProps={{ fontSize: "16px" }}
                    inputStyles={{ bg: "white", width: "100%" }}
                  />
                  <Flex align="center" justify={["center"]} mt={[6]} mr="24px">
                    <Button
                      variant="linkButton"
                      fontSize="16px"
                      fontWeight={"normal"}
                      color="white"
                      mr="2"
                      _hover={{ bg: "transparent" }}
                      onClick={onBack}
                    >
                      Back
                    </Button>
                    <Button variant="Button1RegularOutline" type="submit" isLoading={resendLoading}>
                      Send
                    </Button>
                  </Flex>
                </form>
              </Box>
            </>
          )}
        </>
      }
      footerComponent={
        <>
          {!resendSuccess && (
            <Text variant={"RBody"} color="white">
              If you still need help, call <a href="tel:1300 76 46 26">1300 76 46 26</a> (local) or <a href="tel:+61 7 3390 5420">+61 7 3390 5420</a>{" "}
              (international)
            </Text>
          )}
        </>
      }
      footerProps={{ alignSelf: "flex-end", maxW: ["328px", "328px", "328px", "362px", "362px"], justifySelf: "center" }}
      middleProps={{ alignSelf: "center", justifySelf: "center" }}
    />
  )
}
